import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PmDocNode } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/core";

import type { BlockWarning } from "@/api/manual/rules";

const lintKey = new PluginKey<Map<string, string>>("documentLint");

function decorate(doc: PmDocNode, flagged: Map<string, string>) {
	const decorations: Decoration[] = [];

	doc.descendants((node, pos) => {
		if (typeof node.attrs.id === "string" && flagged.has(node.attrs.id)) {
			decorations.push(
				Decoration.node(pos, pos + node.nodeSize, {
					class: "lint-flag",
					title: flagged.get(node.attrs.id),
				}),
			);
		}

		return true;
	});

	return DecorationSet.create(doc, decorations);
}

export const LintExtension = Extension.create({
	name: "documentLint",

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: lintKey,
				state: {
					init: () => new Map<string, string>(),
					apply: (tr, flagged) => tr.getMeta(lintKey) ?? flagged,
				},
				props: {
					decorations(state) {
						return decorate(state.doc, lintKey.getState(state) ?? new Map());
					},
				},
			}),
		];
	},
});

export function setLintFlags(editor: Editor, warnings: BlockWarning[]) {
	const flagged = new Map<string, string>();

	for (const warning of warnings) {
		const line = `${warning.term}: ${warning.suggestion}`;
		const current = flagged.get(warning.blockId);

		flagged.set(warning.blockId, current === undefined ? line : `${current}\n${line}`);
	}

	editor.view.dispatch(editor.state.tr.setMeta(lintKey, flagged));
}
